import { useMemo, useState } from "react";
import {
    Button,
    Checkbox,
    CircularProgress,
    Container,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import { makeStyles } from "@/compat/muiStyles";
import { ArrowBack, ArrowForward, Close, Search } from "@mui/icons-material";
import clsx from "clsx";
import { useSnackbar } from "notistack";
import { useHistory } from "@/compat/router";
import { useAppSelector } from "@/redux/hooks";
import { KeyboardDatePicker } from "@/compat/pickers";
import CardBase from "../../../components/cards/CardBase";
import ResponsiveSelect from "../../../components/ui/ResponsiveSelect";
import RiskAssessmentCompletedCard from "../../components/riskassessments/RiskAssessmentCompletedCard";
import useContactsForAppAndSite from "../../../hooks/useContactsForAppAndSite";
import useAddRiskAssessment from "../../hooks/mutations/useAddRiskAssessment";
import useAllModuleTemplates from "../../hooks/queries/useAllModuleTemplates";
import { formatISODateTime } from "../../../utils/dateTimeUtils";
import { frequencyDropdownValues } from "../../constants/frequencyConstants";
import moduleAreas, { moduleUrls } from "../../constants/moduleAreaConstants";
import { baseRoute } from "../../routes";

const CATEGORY_ALL = "__all__";
const CATEGORY_COMPANY = "__company__";

type SelectedTemplate = {
    id: number;
    name: string;
    override: boolean;
    siteId: string;
    employeeId: string;
    frequencyId: number | "";
    startDate: Date | null;
};

const useStyles = makeStyles((theme) => ({
    section: { marginBottom: theme.spacing(2) },
    search: { marginBottom: theme.spacing(2) },
    overrideToggle: { marginTop: theme.spacing(1) },
    // Modern Grid offsets its own row spacing, so the container needs clear headroom
    // or the first field's floating label lands on the toggle above it.
    overrideGrid: { marginTop: theme.spacing(3), marginBottom: theme.spacing(1) },
    layout: {
        display: "grid",
        gap: theme.spacing(3),
        gridTemplateColumns: "1fr",
        [theme.breakpoints.up("md")]: { gridTemplateColumns: "240px 1fr" },
    },
    sidebar: { display: "flex", flexDirection: "column", gap: theme.spacing(0.5) },
    catButton: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        width: "100%",
        font: "inherit",
        color: theme.palette.text.primary,
        textAlign: "left",
        padding: theme.spacing(1, 1.25),
        border: "none",
        borderRadius: theme.spacing(1),
        backgroundColor: "transparent",
        cursor: "pointer",
        "&:hover": { backgroundColor: theme.palette.action.hover },
    },
    catButtonActive: { backgroundColor: "rgba(88, 72, 201, 0.10)", color: theme.palette.secondary.main, fontWeight: 600 },
    catLabel: { flex: 1, fontSize: "0.85rem" },
    catCount: { color: theme.palette.text.secondary, fontSize: "0.8rem", fontVariantNumeric: "tabular-nums" },
    catDivider: { margin: theme.spacing(1, 0) },
    listWrap: { maxHeight: 420, overflowY: "auto" },
    templateRow: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        padding: theme.spacing(0.5, 0),
    },
    templateName: { flex: 1, fontSize: "0.9rem" },
    templateSector: { color: theme.palette.text.secondary, fontSize: "0.78rem" },
    empty: { color: theme.palette.text.secondary, padding: theme.spacing(3), textAlign: "center" },
    selectedCount: { color: theme.palette.text.secondary, marginBottom: theme.spacing(1) },
    itemCard: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    itemHeader: { display: "flex", alignItems: "center", gap: theme.spacing(1.5) },
    itemName: { flex: 1, fontWeight: 600 },
    footer: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: theme.spacing(2),
        padding: theme.spacing(2),
        marginTop: theme.spacing(2),
        borderTop: `1px solid ${theme.palette.divider}`,
    },
}));

export default function AddRiskAssessment() {
    const classes = useStyles();
    const history = useHistory();
    const { enqueueSnackbar } = useSnackbar();

    const { sites, selectedSite, activeAppId, appSiteIds, selectedAccount } = useAppSelector((state) => state.account);

    const [siteId, setSiteId] = useState<string>(selectedSite?.externalId ?? "");
    const [employeeId, setEmployeeId] = useState<string>("");
    const [frequencyId, setFrequencyId] = useState<number | "">("");
    const [startDate, setStartDate] = useState<Date | null>(new Date());
    const [selected, setSelected] = useState<SelectedTemplate[]>([]);
    const [search, setSearch] = useState("");
    const [activeCategory, setActiveCategory] = useState<string>(CATEGORY_ALL);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [createdResult, setCreatedResult] = useState<{ id?: number; name: string }[] | null>(null);

    const addRiskAssessment = useAddRiskAssessment();
    const allContacts = useContactsForAppAndSite({ selectedSiteId: null });
    const contactsList = Object.values(allContacts?.data ?? {}) as any[];
    const templatesQuery = useAllModuleTemplates({
        moduleArea: moduleAreas.RISKASSESSMENTS,
        accountId: selectedAccount?.account?.externalId ?? "",
        siteExternalIds: selectedSite ? [selectedSite.externalId] : appSiteIds[activeAppId],
    });
    const templates: any[] = templatesQuery?.data ?? [];

    const raBase = `${baseRoute}/${moduleUrls[moduleAreas.RISKASSESSMENTS]}`;
    const siteOptions = Object.values(sites ?? {});

    const employeesForSite = (site: string) =>
        contactsList
            .filter((c) => !site || c.accountIds?.includes(site))
            .sort((a, b) => (a.firstName ?? "").localeCompare(b.firstName ?? ""));

    const sectors = useMemo(() => {
        const map = new Map<string, number>();
        templates.forEach((t) => {
            const s = t.sector || "Other";
            map.set(s, (map.get(s) ?? 0) + 1);
        });
        return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
    }, [templates]);
    const companyCount = templates.filter((t) => t.siteExternalId).length;

    const term = search.trim().toLowerCase();
    const visibleTemplates = useMemo(() => {
        if (term) return templates.filter((t) => t.name.toLowerCase().includes(term));
        if (activeCategory === CATEGORY_ALL) return templates;
        if (activeCategory === CATEGORY_COMPANY) return templates.filter((t) => t.siteExternalId);
        return templates.filter((t) => (t.sector || "Other") === activeCategory);
    }, [templates, term, activeCategory]);

    const isSelected = (id: number) => selected.some((s) => s.id === id);

    const toggleTemplate = (t: any) => {
        setSelected((prev) =>
            prev.some((s) => s.id === t.id)
                ? prev.filter((s) => s.id !== t.id)
                : [
                      ...prev,
                      { id: t.id, name: t.name, override: false, siteId: "", employeeId: "", frequencyId: "", startDate: null },
                  ]
        );
    };

    const updateItem = (id: number, patch: Partial<SelectedTemplate>) =>
        setSelected((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));

    const toggleOverride = (id: number) =>
        setSelected((prev) =>
            prev.map((s) => {
                if (s.id !== id) return s;
                if (s.override) return { ...s, override: false };
                // Seed per-template fields from the shared defaults so only differences need editing.
                return { ...s, override: true, siteId, employeeId, frequencyId, startDate };
            })
        );

    const effective = (s: SelectedTemplate) =>
        s.override
            ? { site: s.siteId, employee: s.employeeId, frequency: s.frequencyId, startDate: s.startDate }
            : { site: siteId, employee: employeeId, frequency: frequencyId, startDate };

    const canSubmit =
        selected.length > 0 &&
        !isSubmitting &&
        selected.every((s) => {
            const e = effective(s);
            return !!e.site && !!e.employee && e.frequency !== "" && !!e.startDate;
        });

    const onContinue = async () => {
        if (!canSubmit) return;
        setIsSubmitting(true);
        try {
            const created: { id?: number; name: string }[] = [];
            for (const item of selected) {
                const e = effective(item);
                const result: any = await addRiskAssessment.mutateAsync({
                    siteExternalId: e.site,
                    assignedEmployeeExternalId: e.employee,
                    templateId: item.id,
                    frequency: e.frequency,
                    startDate: formatISODateTime(e.startDate),
                });
                created.push({ id: result?.id, name: item.name });
            }
            // One template → straight into evaluation; several → confirmation, complete later.
            if (created.length === 1 && created[0]?.id) {
                history.push(`${raBase}/complete/${created[0].id}`);
            } else {
                setCreatedResult(created);
            }
        } catch (err) {
            console.error(err);
            enqueueSnackbar("Could not create the risk assessment(s). Please try again.", { variant: "error" });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (createdResult) {
        const count = createdResult.length;
        return (
            <RiskAssessmentCompletedCard
                heading={`${count} risk assessment${count === 1 ? "" : "s"} created`}
                subtitle="They're in your register, ready to complete when you are."
                onView={() => history.push(raBase)}
                primaryLabel="Go to Risk Assessments"
                onBack={() => history.push(raBase)}
            />
        );
    }

    return (
        <Container>
            <div className={classes.section}>
                <CardBase title="Assessment Details" description="Applied to every template you select (customise per template below).">
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 3 }}>
                            <ResponsiveSelect
                                size="small"
                                required
                                fullWidth
                                label="Select a Site"
                                options={siteOptions}
                                optionValueKey="externalId"
                                optionLabelKey="name"
                                value={siteId}
                                onChange={(e: any) => {
                                    setSiteId(e.target.value);
                                    setEmployeeId("");
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 3 }}>
                            <ResponsiveSelect
                                size="small"
                                required
                                fullWidth
                                label="Responsible Employee"
                                options={employeesForSite(siteId)}
                                optionValueKey="externalId"
                                optionLabelFunction={(x: any) => `${x.firstName} ${x.lastName}`}
                                value={employeeId}
                                onChange={(e: any) => setEmployeeId(e.target.value)}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 3 }}>
                            <ResponsiveSelect
                                size="small"
                                required
                                fullWidth
                                label="Review Frequency"
                                options={frequencyDropdownValues}
                                optionValueKey="value"
                                optionLabelKey="label"
                                value={frequencyId}
                                onChange={(e: any) => setFrequencyId(e.target.value)}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 3 }}>
                            <KeyboardDatePicker
                                format="dd/MM/yyyy"
                                size="small"
                                required
                                fullWidth
                                label="Start Date"
                                inputVariant="outlined"
                                value={startDate}
                                onChange={(date: Date | null) => setStartDate(date)}
                            />
                        </Grid>
                    </Grid>
                </CardBase>
            </div>

            <div className={classes.section}>
                <CardBase title="Select Template" description="Choose one or more templates to create risk assessments from.">
                    <TextField
                        fullWidth
                        size="small"
                        variant="outlined"
                        placeholder="Search all templates"
                        className={classes.search}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search fontSize="small" />
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />

                    <div className={classes.layout}>
                        <div className={classes.sidebar}>
                            <button
                                type="button"
                                className={clsx(classes.catButton, { [classes.catButtonActive]: !term && activeCategory === CATEGORY_ALL })}
                                onClick={() => {
                                    setSearch("");
                                    setActiveCategory(CATEGORY_ALL);
                                }}
                            >
                                <span className={classes.catLabel}>All templates</span>
                                <span className={classes.catCount}>{templates.length}</span>
                            </button>
                            {sectors.map(([name, count]) => (
                                <button
                                    key={name}
                                    type="button"
                                    className={clsx(classes.catButton, { [classes.catButtonActive]: !term && activeCategory === name })}
                                    onClick={() => {
                                        setSearch("");
                                        setActiveCategory(name);
                                    }}
                                >
                                    <span className={classes.catLabel}>{name}</span>
                                    <span className={classes.catCount}>{count}</span>
                                </button>
                            ))}
                            {companyCount > 0 && (
                                <>
                                    <Divider className={classes.catDivider} />
                                    <button
                                        type="button"
                                        className={clsx(classes.catButton, { [classes.catButtonActive]: !term && activeCategory === CATEGORY_COMPANY })}
                                        onClick={() => {
                                            setSearch("");
                                            setActiveCategory(CATEGORY_COMPANY);
                                        }}
                                    >
                                        <span className={classes.catLabel}>Company specific</span>
                                        <span className={classes.catCount}>{companyCount}</span>
                                    </button>
                                </>
                            )}
                        </div>

                        <div>
                            {templatesQuery?.isLoading ? (
                                <div className={classes.empty}>
                                    <CircularProgress size={24} color="primary" />
                                </div>
                            ) : visibleTemplates.length === 0 ? (
                                <Typography className={classes.empty}>
                                    {term ? "No templates match your search." : "No templates in this category."}
                                </Typography>
                            ) : (
                                <div className={classes.listWrap}>
                                    {visibleTemplates.map((t) => (
                                        <div className={classes.templateRow} key={t.id}>
                                            <Checkbox size="small" checked={isSelected(t.id)} onChange={() => toggleTemplate(t)} />
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <Typography className={classes.templateName}>{t.name}</Typography>
                                                {t.sector && (
                                                    <Typography className={classes.templateSector}>{t.sector}</Typography>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </CardBase>
            </div>

            {selected.length > 0 && (
                <div className={classes.section}>
                    <CardBase title={`Selected templates (${selected.length})`}>
                        {selected.map((item) => (
                            <div className={classes.itemCard} key={item.id}>
                                <div className={classes.itemHeader}>
                                    <Typography className={classes.itemName}>{item.name}</Typography>
                                    <IconButton size="small" aria-label={`Remove ${item.name}`} onClick={() => toggleTemplate({ id: item.id })}>
                                        <Close fontSize="small" />
                                    </IconButton>
                                </div>
                                <FormControlLabel
                                    className={classes.overrideToggle}
                                    control={
                                        <Switch size="small" color="secondary" checked={item.override} onChange={() => toggleOverride(item.id)} />
                                    }
                                    label="Use different details for this template"
                                />
                                {item.override && (
                                    <Grid container spacing={2} className={classes.overrideGrid}>
                                        <Grid size={{ xs: 12, md: 3 }}>
                                            <ResponsiveSelect
                                                size="small"
                                                required
                                                fullWidth
                                                label="Select a Site"
                                                options={siteOptions}
                                                optionValueKey="externalId"
                                                optionLabelKey="name"
                                                value={item.siteId}
                                                onChange={(e: any) => updateItem(item.id, { siteId: e.target.value, employeeId: "" })}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12, md: 3 }}>
                                            <ResponsiveSelect
                                                size="small"
                                                required
                                                fullWidth
                                                label="Responsible Employee"
                                                options={employeesForSite(item.siteId)}
                                                optionValueKey="externalId"
                                                optionLabelFunction={(x: any) => `${x.firstName} ${x.lastName}`}
                                                value={item.employeeId}
                                                onChange={(e: any) => updateItem(item.id, { employeeId: e.target.value })}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12, md: 3 }}>
                                            <ResponsiveSelect
                                                size="small"
                                                required
                                                fullWidth
                                                label="Review Frequency"
                                                options={frequencyDropdownValues}
                                                optionValueKey="value"
                                                optionLabelKey="label"
                                                value={item.frequencyId}
                                                onChange={(e: any) => updateItem(item.id, { frequencyId: e.target.value })}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12, md: 3 }}>
                                            <KeyboardDatePicker
                                                format="dd/MM/yyyy"
                                                size="small"
                                                required
                                                fullWidth
                                                label="Start Date"
                                                inputVariant="outlined"
                                                value={item.startDate}
                                                onChange={(date: Date | null) => updateItem(item.id, { startDate: date })}
                                            />
                                        </Grid>
                                    </Grid>
                                )}
                            </div>
                        ))}
                    </CardBase>
                </div>
            )}

            <div className={classes.footer}>
                <Button variant="outlined" color="primary" startIcon={<ArrowBack />} onClick={() => history.push(`${raBase}/add`)}>
                    Back
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    endIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : <ArrowForward />}
                    disabled={!canSubmit}
                    onClick={onContinue}
                >
                    Continue
                </Button>
            </div>
        </Container>
    );
}
